import { normalizePath } from '../routes';
import { useTheme } from '../design/ThemeProvider';
import { Settings, Palette, LogOut, Mic, Folder, User } from 'lucide-react';

interface NavigationProps {
  currentPath: string;
  onNavigate: (path: string, requiresAuth?: boolean) => void;
  isAuthenticated: boolean;
  onLoginClick: () => void;
  onSignOut: () => void;
  profilePath?: string;
  profileImageUrl?: string | null;
  showLibrary: boolean;
  libraryPath?: string;
  showCreator: boolean;
  creatorPath?: string;
  creatorLandingPath?: string | null;
  variant?: 'header' | 'footer';
}

const AVATAR_PLACEHOLDER =
  'https://images.unsplash.com/photo-1504593811423-6dd665756598?auto=format&fit=crop&w=200&q=80';

export function Navigation({
  currentPath,
  onNavigate,
  isAuthenticated,
  onLoginClick,
  onSignOut,
  profilePath,
  profileImageUrl,
  showLibrary,
  libraryPath,
  showCreator,
  creatorPath,
  creatorLandingPath,
  variant = 'footer',
}: NavigationProps) {
  const {
    enabledThemes,
    activeTheme,
    setActiveTheme,
    saving: themeSaving,
    saveError: themeSaveError,
  } = useTheme();
  const tabs = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'explore', label: 'Explore', path: '/explore' },
  ];

  if (showLibrary && libraryPath) {
    tabs.push({ id: 'library', label: 'Library', path: libraryPath, requiresAuth: true });
  }

  const normalizedActive = normalizePath(currentPath);
  const isFooter = variant === 'footer';
  const avatarSrc = isAuthenticated && profileImageUrl ? profileImageUrl : AVATAR_PLACEHOLDER;
  const baseItemClass = isFooter
    ? 'text-[11px] uppercase tracking-[0.2em] px-2 py-2'
    : 'text-sm px-2 py-2';

  const isPathActive = (path: string) => {
    const normalizedTarget = normalizePath(path);
    if (normalizedTarget === '/') {
      return normalizedActive === '/';
    }
    return normalizedActive === normalizedTarget || normalizedActive.startsWith(`${normalizedTarget}/`);
  };

  const profileActive = profilePath
    ? isPathActive(profilePath)
    : normalizedActive.startsWith('/profile');

  return (
    <nav
      className={
        isFooter
          ? 'fixed bottom-0 left-0 right-0 bg-base-100/90 backdrop-blur-xl border-t border-base-300 z-40 md:hidden'
          : 'hidden md:block sticky top-0 z-40 bg-base-100/90 backdrop-blur-xl border-b border-base-300'
      }
    >
      <div className="max-w-[1200px] mx-auto px-4">
        <div className={`flex items-center ${isFooter ? 'justify-between h-20 gap-4' : 'justify-between h-16'}`}>
          <div className={`flex ${isFooter ? 'flex-1 justify-around' : 'gap-6'}`}>
            {tabs.map((tab) => {
              const isActive = isPathActive(tab.path);

              return (
                <button
                  key={tab.id}
                  onClick={() => onNavigate(tab.path, tab.requiresAuth)}
                  className={`${baseItemClass} transition-colors ${
                    isActive ? 'text-base-content font-semibold' : 'text-base-content/60 hover:text-base-content'
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <div className={`dropdown ${isFooter ? 'dropdown-top dropdown-end' : 'dropdown-end'}`}>
                <div
                  tabIndex={0}
                  role="button"
                  className={`inline-flex items-center gap-2 ${profileActive ? 'text-base-content' : 'text-base-content/80 hover:text-base-content'}`}
                  aria-label="Open account menu"
                >
                  <span className="avatar">
                    <span className="w-8 rounded-full overflow-hidden ring-1 ring-base-300">
                      <img src={avatarSrc} alt="Profile avatar" className="h-full w-full object-cover" />
                    </span>
                  </span>
                  {!isFooter && <span className="text-sm">Account</span>}
                </div>
                <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-60">
                  <li>
                    <button
                      type="button"
                      onClick={() => {
                        if (profilePath) {
                          onNavigate(profilePath, true);
                        }
                      }}
                    >
                      <Settings className="w-4 h-4" />
                      Account settings
                    </button>
                  </li>
                  {showCreator && creatorPath && (
                    <li>
                      <button type="button" onClick={() => onNavigate(creatorPath, true)}>
                        <Folder className="w-4 h-4" />
                        Manage series
                      </button>
                    </li>
                  )}
                  {showCreator && creatorLandingPath && (
                    <li>
                      <button type="button" onClick={() => onNavigate(creatorLandingPath)}>
                        <Mic className="w-4 h-4" />
                        My Creator page
                      </button>
                    </li>
                  )}
                  <li>
                    <details>
                      <summary>
                        <Palette className="w-4 h-4" />
                        Theme
                      </summary>
                      <ul>
                        {enabledThemes.map((theme) => (
                          <li key={theme}>
                            <label className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={isFooter ? 'theme-footer' : 'theme-header'}
                                className="theme-controller radio radio-sm"
                                value={theme}
                                checked={activeTheme === theme}
                                disabled={themeSaving}
                                onChange={() => setActiveTheme(theme)}
                              />
                              <span className="text-sm capitalize">{theme}</span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    </details>
                  </li>
                  {themeSaveError && (
                    <li>
                      <span className="text-xs text-error">{themeSaveError}</span>
                    </li>
                  )}
                  <li className="mt-2 border-t border-base-200 pt-2">
                    <button type="button" onClick={onSignOut}>
                      <LogOut className="w-4 h-4" />
                      Log out
                    </button>
                  </li>
                </ul>
              </div>
            ) : (
              <button
                onClick={onLoginClick}
                className={`${baseItemClass} text-base-content`}
                aria-label="Login or register"
              >
                <User className="w-4 h-4 inline-block mr-2" />
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
